/**
 * The Gaps tab (gaps plan §8).
 *
 * The header is three separate numbers, not one total, and that is the whole
 * point of the screen. The results card reduces "incomplete" to `78% · 327
 * open` and two hollow dots, which are three unrelated facts wearing one
 * feeling; a single "open gaps" figure here would rebuild exactly that.
 * `18 parsed · 22 never read · 4 stalled · of 44 capture holds` says the
 * thing the card cannot.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import type { GapsResponse } from '@server/sql/gaps.ts'
import type { ActionSpec } from '@server/jobs/actions.ts'
import type { JobRecord } from '@server/jobs/store.ts'
import { ApiError, api, keys } from '~/lib/api.ts'
import { useT } from '~/i18n/index.tsx'
import { count, timestamp } from '~/i18n/format.ts'
import { Button, Skeleton } from '~/components/ui/primitives.tsx'
import { Disclosure } from '~/components/ui/disclosure.tsx'
import { ErrorState } from '~/components/ui/empty.tsx'
import { EstimateDialog } from '~/features/jobs/estimate-dialog.tsx'
import { JobPanel } from '~/features/jobs/job-panel.tsx'
import { GapGroups } from './gap-list.tsx'

const TERMINAL = new Set(['done', 'failed', 'cancelled', 'interrupted'])

export function GapsPanel({ symbol }: { symbol: string }) {
  const { t, locale } = useT()
  const qc = useQueryClient()
  /** The action awaiting approval. Null closes the dialog. */
  const [pending, setPending] = useState<{ spec: ActionSpec; command: string } | null>(null)

  const { data: jobList } = useQuery({
    queryKey: keys.jobs(symbol),
    queryFn: () => api<{ jobs: JobRecord[]; enabled: boolean }>(
      `/api/jobs?symbol=${encodeURIComponent(symbol)}`),
    refetchInterval: 10_000,
  })
  const actionsEnabled = jobList?.enabled ?? false
  const active = (jobList?.jobs ?? []).filter((j) => !TERMINAL.has(j.status))
  const earlier = (jobList?.jobs ?? []).filter((j) => TERMINAL.has(j.status))
  const { data, error, refetch } = useQuery({
    queryKey: keys.gaps(symbol),
    queryFn: () => api<GapsResponse>(`/api/gaps/${encodeURIComponent(symbol)}`),
    // The answer changes when a job runs, not on its own; this matches the
    // server's own cache, so a refetch on focus is usually free.
    staleTime: 30_000,
  })

  if (error) {
    const e = error as ApiError
    return (
      <ErrorState
        title={t('common.error')}
        detail={e.message}
        requestId={e.requestId}
        onRetry={() => void refetch()}
        retryLabel={t('action.retry')}
      />
    )
  }
  if (!data) return <Skeleton className="h-72 w-full" />

  const s = data.summary
  const n = (x: number) => count(x, locale)

  return (
    <div className="space-y-4">
      <div>
        <div className="tnum flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[length:var(--text-data)] text-[var(--text-muted)]">
          <span className="text-[var(--pass)]">
            {t('gaps.summary.complete', { n: n(s.documentsComplete) })}
          </span>
          {s.documentsMissing > 0 && (
            <>
              <Sep />
              <span className="text-[var(--fail)]">
                {t('gaps.summary.missing', { n: n(s.documentsMissing) })}
              </span>
            </>
          )}
          {s.documentsStalled > 0 && (
            <>
              <Sep />
              <span className="text-[var(--fail)]">
                {t('gaps.summary.stalled', { n: n(s.documentsStalled) })}
              </span>
            </>
          )}
          <Sep />
          <span>{t('gaps.summary.expected', { n: n(s.documentsExpected) })}</span>
          {s.openReviewItems > 0 && (
            <>
              <Sep />
              <span className="text-[var(--warn)]">
                {t('gaps.summary.review', {
                  items: n(s.openReviewItems),
                  pages: n(s.pagesFailing),
                })}
              </span>
            </>
          )}
          <Sep />
          <span className="text-[var(--text-faint)]">
            {t('gaps.staleness', { when: timestamp(data.generatedAt, locale) })}
          </span>
        </div>

        {/* Said once, where it stops a reader concluding the app under-reports. */}
        <p className="mt-1.5 max-w-[78ch] text-[length:var(--text-data)] leading-[var(--text-data--line-height)] text-[var(--text-faint)]">
          {t('gaps.hollowDot')}
        </p>

        {!actionsEnabled && (
          <p className="mt-1.5 max-w-[78ch] text-[length:var(--text-data)] leading-[var(--text-data--line-height)] text-[var(--text-muted)]">
            <span className="font-medium text-[var(--text)]">{t('job.disabled')}. </span>
            {t('job.disabledWhy')}
          </p>
        )}
      </div>

      {/*
        The expensive button, and it never fires directly: it opens the same
        estimate every row does, with larger numbers. ~22 documents and hours
        of wall time is a plan to approve, not a button to press (§7).
      */}
      {s.documentsIncomplete > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="primary"
            onClick={() => setPending({
              spec: { kind: 'fill_company', symbol },
              command: `python -m parser.run --symbol ${symbol}`,
            })}
          >
            {t('action.fillAll')}
          </Button>
        </div>
      )}

      {active.length > 0 && (
        <div className="space-y-1">
          {active.map((j) => <JobPanel key={j.id} job={j} />)}
        </div>
      )}

      <GapGroups
        symbol={symbol}
        groups={data.groups}
        onRun={(spec, command) => setPending({ spec, command })}
        actionsEnabled={actionsEnabled}
      />

      {earlier.length > 0 && (
        <Disclosure summary={t('job.earlier')} count={earlier.length}>
          <div className="space-y-1 pb-2 pt-1">
            {earlier.slice(0, 20).map((j) => <JobPanel key={j.id} job={j} compact />)}
          </div>
        </Disclosure>
      )}

      <EstimateDialog
        action={pending?.spec ?? null}
        command={pending?.command ?? null}
        open={pending !== null}
        onClose={() => setPending(null)}
        enabled={actionsEnabled}
        onStarted={() => {
          void qc.invalidateQueries({ queryKey: keys.jobs(symbol) })
          void qc.invalidateQueries({ queryKey: keys.jobs() })
        }}
      />
    </div>
  )
}

const Sep = () => <span className="text-[var(--border-strong)]">·</span>
